import { useTranslation, Trans } from 'next-i18next';
import Link from 'next/link';
import { cn, config } from '@restworld/utils-common';

import { useReferralCredits } from './use-referral-credits';

const STEPS = [
  { emoji: '\uD83D\uDD17', titleKey: 'step_1_title', descriptionKey: 'step_1_description' },
  { emoji: '\uD83E\uDDD1\u200D\uD83C\uDF73', titleKey: 'step_2_title', descriptionKey: 'step_2_description' },
  { emoji: '\uD83C\uDF89', titleKey: 'step_3_title', descriptionKey: 'step_3_description' },
];

/**
 * "How does it work?" section with 3 step cards and a link to regulations/terms.
 */
export function ReferralHowItWorks() {
  const { t } = useTranslation('referral-page');
  const { inviter, invitee, inviteeValueEur } = useReferralCredits();
  const creditValues = { inviter, invitee, inviteeValue: inviteeValueEur };

  return (
    <SectionContainer>
      <SectionTitle>{t('how_it_works_title')}</SectionTitle>

      <StepsWrapper>
        <StepsRow>
          {STEPS.map((step) => (
            <StepCard key={step.titleKey}>
              <StepContent>
                <EmojiCircle>
                  <Emoji>{step.emoji}</Emoji>
                </EmojiCircle>
                <StepTitle>{t(step.titleKey, creditValues)}</StepTitle>
                <StepDescription>{t(step.descriptionKey, creditValues)}</StepDescription>
              </StepContent>
            </StepCard>
          ))}
        </StepsRow>

        <RegulationsText>
          <Trans
            i18nKey="regulations_link_text"
            ns="referral-page"
            components={{
              regulations: <InternalLink href="/employer/referral/rules" target="_blank" rel="noopener noreferrer" />,
              terms: <RegulationsLink href={config.employerTermsUrl} target="_blank" rel="noopener noreferrer" />,
            }}
          />
        </RegulationsText>
      </StepsWrapper>
    </SectionContainer>
  );
}

export default ReferralHowItWorks;

// Layout Components
const SectionContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center w-full py-10 px-4 lg:px-20 gap-10', className)} {...props} />
);

const SectionTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h2 className={cn('text-xl font-semibold text-[#1C1C33] leading-[1.21]', className)} {...props} />
);

const StepsWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full gap-6', className)} {...props} />
);

const StepsRow = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col lg:flex-row gap-5 w-full', className)} {...props} />
);

const StepCard = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex-1 flex flex-col items-center justify-center p-10 sm:p-5 bg-[#F5F5F6] rounded-2xl border border-neutral-200',
      className
    )}
    style={{ boxShadow: '0px 2px 4px 0px rgba(0, 0, 26, 0.05)' }}
    {...props}
  />
);

const StepContent = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col items-center justify-center gap-5', className)} {...props} />
);

const EmojiCircle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex h-[64px] w-[64px] shrink-0 items-center justify-center p-3 bg-[#F0EBF8] rounded-full border border-[#482485]',
      className
    )}
    {...props}
  />
);

const Emoji = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-[30px] font-semibold leading-[1.21]', className)} {...props} />
);

const StepTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h3 className={cn('text-lg font-semibold text-[#1C1C33] text-center leading-[1.21]', className)} {...props} />
);

const StepDescription = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-sm font-medium text-[#1C1C33] text-center leading-relaxed', className)} {...props} />
);

const RegulationsText = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <p className={cn('text-sm font-medium text-[#666676] text-center leading-[1.21]', className)} {...props} />
);

const RegulationsLink = ({ className, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
  <a className={cn('underline text-[#666676] hover:text-[#482485]', className)} {...props} />
);

const InternalLink = ({ className, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string }) => (
  <Link className={cn('underline text-[#666676] hover:text-[#482485]', className)} {...(props as any)} />
);
